// Waterfalls — render-only cascade where an edge river drops >= 2 terrain
// levels (see waterfallEdges.ts for which edges qualify).
//
// Perf (renderer-crash-rca rules): TWO draw calls total regardless of fall
// count — one InstancedMesh of a shared 4-vertex quad for the falling sheet,
// one for the base mist — each with a module-level shared ShaderMaterial.
// Animation is entirely in the shader off a single `uTime` uniform; instance
// matrices are written once per derivation (no per-frame allocation or matrix
// updates). Honors prefers-reduced-motion (time frozen) and the particle
// effects setting (mist off, time frozen).

import type { SceneNode } from '@aod/protocol';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSettings } from '../settings/useSettings.js';
import { LEVEL_STEP, tileSurfaceY } from './elevation.js';
import { type Waterfall, deriveWaterfalls } from './waterfallEdges.js';
import { MIST_FRAG, MIST_VERT, SHEET_FRAG, SHEET_VERT } from './waterfallShaders.js';

/** Shared flow clock (seconds). */
const uTime = { value: 0 };

const sheetGeo = new THREE.PlaneGeometry(1, 1);
const mistGeo = new THREE.PlaneGeometry(1, 1);

const sheetMaterial = new THREE.ShaderMaterial({
  uniforms: { uTime },
  vertexShader: SHEET_VERT,
  fragmentShader: SHEET_FRAG,
  transparent: true,
  depthWrite: false,
  side: THREE.DoubleSide,
});
sheetMaterial.customProgramCacheKey = () => 'waterfall-sheet';

const mistMaterial = new THREE.ShaderMaterial({
  uniforms: { uTime },
  vertexShader: MIST_VERT,
  fragmentShader: MIST_FRAG,
  transparent: true,
  depthWrite: false,
});
mistMaterial.customProgramCacheKey = () => 'waterfall-mist';

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

const _obj = new THREE.Object3D();

/** Write sheet + mist instance matrices once per derivation. */
function fillInstances(
  falls: readonly Waterfall[],
  sheets: THREE.InstancedMesh,
  mist: THREE.InstancedMesh | null,
): void {
  falls.forEach((f, i) => {
    const h = f.topY - f.bottomY;
    // Sheet sits just outside the high tile's edge so it hugs the cliff face.
    _obj.position.set(f.x + f.nx * 0.03, (f.topY + f.bottomY) / 2, f.z + f.nz * 0.03);
    _obj.rotation.set(0, f.yaw, 0);
    _obj.scale.set(f.width, h, 1);
    _obj.updateMatrix();
    sheets.setMatrixAt(i, _obj.matrix);
    // Mist: billboarded in the shader; scale.x = puff size.
    _obj.position.set(f.x + f.nx * 0.1, f.bottomY + 0.04, f.z + f.nz * 0.1);
    _obj.rotation.set(0, 0, 0);
    _obj.scale.set(f.width * 1.7, f.width * 1.7, 1);
    _obj.updateMatrix();
    mist?.setMatrixAt(i, _obj.matrix);
  });
  sheets.count = falls.length;
  if (mist) mist.count = falls.length;
  sheets.instanceMatrix.needsUpdate = true;
  if (mist) mist.instanceMatrix.needsUpdate = true;
}

export function Waterfalls({
  tiles,
  biomeMap,
  visibleCoords,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
  readonly visibleCoords?: ReadonlySet<string> | null;
}) {
  const particleEffects = useSettings((s) => s.particleEffects);
  const reduced = useMemo(prefersReducedMotion, []);
  const sheetRef = useRef<THREE.InstancedMesh>(null);
  const mistRef = useRef<THREE.InstancedMesh>(null);

  const falls = useMemo(
    () =>
      deriveWaterfalls(tiles, {
        levelStep: LEVEL_STEP,
        surfaceY: (q, r) => tileSurfaceY(q, r, biomeMap),
        centre: (q, r) => [Math.sqrt(3) * (q + r / 2), 1.5 * r],
        visible: visibleCoords,
      }),
    [tiles, biomeMap, visibleCoords],
  );

  useEffect(() => {
    if (sheetRef.current) fillInstances(falls, sheetRef.current, mistRef.current);
  }, [falls, particleEffects]);

  useFrame((_, delta) => {
    if (falls.length > 0 && !reduced && particleEffects) uTime.value += delta;
  });

  if (falls.length === 0) return null;
  // Capacity = derivation cap, so the buffers never need re-allocating.
  const capacity = Math.max(falls.length, 1);
  return (
    <group name="waterfalls">
      <instancedMesh
        key={`s${capacity}`}
        ref={sheetRef}
        name="waterfall-sheets"
        args={[sheetGeo, sheetMaterial, capacity]}
        frustumCulled={false}
        raycast={() => null}
        renderOrder={4}
      />
      {particleEffects && (
        <instancedMesh
          key={`m${capacity}`}
          ref={mistRef}
          name="waterfall-mist"
          args={[mistGeo, mistMaterial, capacity]}
          frustumCulled={false}
          raycast={() => null}
          renderOrder={5}
        />
      )}
    </group>
  );
}
